
require("./../../node_modules/@module-federation/nextjs-mf/src/include-defaults.js");
import React, { useState, useEffect, useMemo } from 'react';
import { IntlProvider } from 'react-intl';
import ApolloConfig from '../../apollo-config';
import { useSDMContextProps, useDetermineLocale } from '../../hooks';
import { SDMContexts } from '@ldp/sdm-contexts';
import styles from './container.module.css';
import { PathNames, contentful } from '@/data/constants';

//Components
import Footer from '@/components/footer';
import Header from '@/components/header/header';
import AnnouncementBarContainer from '@/components/announcement-bar/announcement-bar-container';
import GlobalState from '@/components/global-state';

import messages, { type Locale } from 'I18n/translations';
import { useRouter } from 'next/router';
import { CMSData } from '@/types/cmsData';

export type HeaderFooterType =
  | {
      en: { navigation: CMSData[]; footer: CMSData[]; navigationMobile: CMSData[] };
      fr: { navigation: CMSData[]; footer: CMSData[]; navigationMobile: CMSData[] };
    }
  | Error
  | undefined;

interface LayoutProps {
  children: React.ReactNode;
  headerFooterData?: HeaderFooterType | null;
  hasError: boolean;
  cartData?: object | null;
}

// These are pathnames after mapping defined in next.config.js
const componentIds = {
  [PathNames.HOME_PAGE]: contentful.COMPONENT_IDS.HOME_LANDING_PAGE,
  [PathNames.BEAUTY_LANDING_PAGE]: contentful.COMPONENT_IDS.BEAUTY_LANDING_PAGE,
  [PathNames.PERSONAL_CARE_LANDING_PAGE]: contentful.COMPONENT_IDS.PERSONAL_CARE_LANDING_PAGE,
  [PathNames.HEALTH_LANDING_PAGE]: contentful.COMPONENT_IDS.HEALTH_LANDING_PAGE,
  [PathNames.BABY_AND_CHILD_LANDING_PAGE]: contentful.COMPONENT_IDS.BABY_AND_CHILD_LANDING_PAGE,
  [PathNames.ELECTRONICS_LANDING_PAGE]: contentful.COMPONENT_IDS.ELECTRONICS_LANDING_PAGE,
} satisfies { [key in PathNames]?: ValueOf<typeof contentful.COMPONENT_IDS> };

export default function Layout({ children, headerFooterData, cartData, hasError }: LayoutProps) {
  const locale = useDetermineLocale();
  const [preferredLocale, setPreferredLocale] = useState<Locale>('en');
  const sdmContextProps = useSDMContextProps();
  const { pathname } = useRouter();

  const componentId = useMemo(
    () =>
      pathname in componentIds
        ? componentIds[pathname as keyof typeof componentIds]
        : contentful.COMPONENT_IDS.LANDING_PAGE,
    [pathname]
  );

  useEffect(() => setPreferredLocale(locale), [locale]);

  return (
    <GlobalState headerFooterData={headerFooterData ?? undefined} cartData={cartData ?? undefined}>
      <SDMContexts {...sdmContextProps}>
        <IntlProvider
          locale={preferredLocale}
          messages={messages[preferredLocale]}
          onError={() => {
            return 'no error';
          }}
        >
          <ApolloConfig>
            <Header hasError={hasError} />
            <AnnouncementBarContainer componentId={componentId} />
            <div className={styles.container} id="main-content">
              {children}
            </div>
          </ApolloConfig>
          <Footer />
        </IntlProvider>
      </SDMContexts>
    </GlobalState>
  );
}
